import { useEffect, useState } from "react";
import { Button } from "@components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@components/ui/empty";
import { Skeleton } from "@components/ui/skeleton";
import { getEntities, type EntitySummary } from "../lib/auth";
import { PageHeader } from "@components/layout/PageHeader";
import { PageShell } from "@components/layout/PageShell";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@components/ui/breadcrumb";
import { SidebarTrigger } from "@components/ui/sidebar";
import { RoutingRationaleSheet } from "@components/routing/RoutingRationaleSheet";

type LensShellPageProps = {
  lensKey: string;
  navigate: (route: string) => void;
};

export function LensShellPage({ lensKey, navigate }: LensShellPageProps) {
  const [lensEntities, setLensEntities] = useState<EntitySummary[]>([]);
  const [isLensLoading, setIsLensLoading] = useState(false);
  const [lensError, setLensError] = useState<string | null>(null);
  const [lensSearch, setLensSearch] = useState("");
  const [lensStatus, setLensStatus] = useState("all");
  const [visibleCount, setVisibleCount] = useState(25);
  const [rationaleEntity, setRationaleEntity] = useState<EntitySummary | null>(
    null,
  );

  useEffect(() => {
    let isActive = true;
    setIsLensLoading(true);
    setLensError(null);
    getEntities()
      .then((entities) => {
        if (!isActive) {
          return;
        }
        setLensEntities(entities);
      })
      .catch((err) => {
        if (!isActive) {
          return;
        }
        const message =
          err instanceof Error ? err.message : "Unable to load lens data.";
        setLensError(message);
      })
      .finally(() => {
        if (isActive) {
          setIsLensLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, []);

  const lensConfigs: Record<
    string,
    { title: string; description: string; defaultStatuses: string[] }
  > = {
    support: {
      title: "Support Lens",
      description: "Intake and triage for proposed work.",
      defaultStatuses: ["new", "proposed"],
    },
    execution: {
      title: "Approved work",
      description: "Execution Lens for approved work and active delivery.",
      defaultStatuses: ["approved", "in_progress"],
    },
    automation: {
      title: "Automation Lens",
      description: "Workflow consistency and repeatability.",
      defaultStatuses: ["approved", "in_progress", "done"],
    },
    leadership: {
      title: "Leadership Lens",
      description: "Portfolio health and risk posture.",
      defaultStatuses: [],
    },
  };
  const lensOrder = ["support", "execution", "automation", "leadership"];
  const lensConfig = lensConfigs[lensKey];

  const normalizedLensSearch = lensSearch.trim().toLowerCase();
  const lensScopedEntities = lensEntities.filter((entity) => {
    if (lensStatus !== "all") {
      return entity.status === lensStatus;
    }
    if (lensConfig?.defaultStatuses.length) {
      return lensConfig.defaultStatuses.includes(entity.status);
    }
    return true;
  });

  const lensFilteredEntities = lensScopedEntities.filter((entity) => {
    if (!normalizedLensSearch) {
      return true;
    }
    const haystack = [
      entity.title,
      entity.description,
      entity.systems.join(" "),
      entity.assignment?.assigneeId ?? "",
      entity.type,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(normalizedLensSearch);
  });

  useEffect(() => {
    setVisibleCount(25);
  }, [lensKey, lensStatus, lensSearch, lensFilteredEntities.length]);

  const visibleEntities = lensFilteredEntities.slice(0, visibleCount);

  const lensOpenEntities = lensFilteredEntities.filter(
    (entity) => entity.status !== "done",
  );
  const lensBlockers = lensOpenEntities.filter(
    (entity) => entity.priority === "high" || entity.priority === "critical",
  );
  const unassignedEntities = lensFilteredEntities.filter(
    (entity) => !entity.assignment?.assigneeId,
  );
  const avgConfidence =
    lensFilteredEntities.length > 0
      ? lensFilteredEntities.reduce(
          (sum, entity) => sum + (entity.confidence ?? 0),
          0,
        ) / lensFilteredEntities.length
      : 0;
  const inProgressEntities = lensFilteredEntities.filter(
    (entity) => entity.status === "in_progress",
  );
  const proposedEntities = lensFilteredEntities.filter(
    (entity) => entity.status === "proposed" || entity.status === "new",
  );
  const highPriorityEntities = lensFilteredEntities.filter(
    (entity) => entity.priority === "high" || entity.priority === "critical",
  );

  const breadcrumb = (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <button type="button" onClick={() => navigate("/dashboard")}>
              Dashboard
            </button>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>Lens</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  return (
    <PageShell maxWidth="6xl">
      <PageHeader
        breadcrumb={breadcrumb}
        leading={<SidebarTrigger />}
        eyebrow="Lens Navigation"
        title={lensConfig?.title ?? "Lens overview"}
        description={
          lensConfig?.description ??
          "Choose a lens to explore work by perspective."
        }
        actions={
          <>
            {lensOrder.map((lens) => (
              <Button
                key={lens}
                variant={lensKey === lens ? "default" : "outline"}
                onClick={() => navigate(`/lens/${lens}`)}
              >
                {lens.charAt(0).toUpperCase() + lens.slice(1)}
              </Button>
            ))}
          </>
        }
      />

      <details className="mt-8 rounded-xl border border-border bg-card/60">
        <summary className="flex min-h-11 cursor-pointer items-center rounded-lg px-4 py-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          Filters and lens overview
        </summary>
        <section className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <Card className="border-border bg-card/60">
            <CardHeader>
              <CardTitle className="text-lg">Shared filters</CardTitle>
              <CardDescription>
                Search and status filters persist as you switch lenses.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="lens-search">Search</Label>
                <Input
                  id="lens-search"
                  type="text"
                  placeholder="Search by title, system, owner..."
                  value={lensSearch}
                  onChange={(event) => setLensSearch(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lens-status">Status</Label>
                <Select value={lensStatus} onValueChange={setLensStatus}>
                  <SelectTrigger id="lens-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="new">new</SelectItem>
                    <SelectItem value="proposed">proposed</SelectItem>
                    <SelectItem value="approved">approved</SelectItem>
                    <SelectItem value="in_progress">in progress</SelectItem>
                    <SelectItem value="done">done</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border bg-card/60">
            <CardHeader>
              <CardTitle className="text-lg">Lens summary</CardTitle>
              <CardDescription>Work in this view.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total in scope</span>
                <span className="text-lg font-semibold">
                  {lensFilteredEntities.length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Open work</span>
                <span className="text-lg font-semibold">
                  {lensOpenEntities.length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Blockers</span>
                <span className="text-lg font-semibold text-amber-800 dark:text-amber-200">
                  {lensBlockers.length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Lens focus</span>
                <span className="text-sm">
                  {lensStatus === "all" && lensConfig?.defaultStatuses.length
                    ? lensConfig.defaultStatuses.join(", ")
                    : lensStatus}
                </span>
              </div>
            </CardContent>
          </Card>
        </section>

        <section className="mt-6 grid gap-6 lg:grid-cols-3">
          {lensKey === "support" ? (
            <Card className="border-border bg-card/60">
              <CardHeader>
                <CardTitle className="text-lg">Response readiness</CardTitle>
                <CardDescription>
                  Customer-facing readiness signals.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">New or proposed</span>
                  <span className="text-lg font-semibold">
                    {proposedEntities.length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Unassigned</span>
                  <span className="text-lg font-semibold">
                    {unassignedEntities.length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    Average confidence
                  </span>
                  <span className="text-lg font-semibold">
                    {avgConfidence.toFixed(2)}
                  </span>
                </div>
              </CardContent>
            </Card>
          ) : null}
          {lensKey === "execution" ? (
            <Card className="border-border bg-card/60">
              <CardHeader>
                <CardTitle className="text-lg">Execution focus</CardTitle>
                <CardDescription>
                  Ownership, progress, and blockers.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">In progress</span>
                  <span className="text-lg font-semibold">
                    {inProgressEntities.length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Unassigned</span>
                  <span className="text-lg font-semibold">
                    {unassignedEntities.length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Blockers</span>
                  <span className="text-lg font-semibold text-amber-800 dark:text-amber-200">
                    {lensBlockers.length}
                  </span>
                </div>
              </CardContent>
            </Card>
          ) : null}
          {lensKey === "leadership" ? (
            <Card className="border-border bg-card/60">
              <CardHeader>
                <CardTitle className="text-lg">Leadership signals</CardTitle>
                <CardDescription>
                  Risk and workload concentration.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Open work</span>
                  <span className="text-lg font-semibold">
                    {lensOpenEntities.length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">High priority</span>
                  <span className="text-lg font-semibold">
                    {highPriorityEntities.length}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Blockers</span>
                  <span className="text-lg font-semibold text-amber-800 dark:text-amber-200">
                    {lensBlockers.length}
                  </span>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </section>
      </details>
      <section className="mt-8">
        <Card className="border-border bg-card/60">
          <CardHeader>
            <CardTitle className="text-lg">Work items</CardTitle>
            <CardDescription>
              Review the work in this view and choose your next action.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLensLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Card
                    key={`lens-skeleton-${index}`}
                    className="border-border bg-card/60"
                  >
                    <CardHeader className="space-y-2">
                      <Skeleton className="h-4 w-28" />
                      <Skeleton className="h-6 w-2/3" />
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-5/6" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : lensError ? (
              <p
                className="text-sm text-red-700 dark:text-red-400"
                role="alert"
              >
                {lensError}
              </p>
            ) : lensFilteredEntities.length === 0 ? (
              <Empty className="border-border/60 bg-muted/40">
                <EmptyHeader>
                  <EmptyTitle>No work items match this lens</EmptyTitle>
                  <EmptyDescription className="text-muted-foreground">
                    Try adjusting the shared filters or lens status.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <>
                {visibleEntities.map((entity) => (
                  <div
                    key={entity.id}
                    className="flex flex-col gap-2 rounded-lg border border-border bg-muted/40 p-4 md:flex-row md:items-center md:justify-between"
                  >
                    <div className="space-y-1">
                      <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground/70">
                        {entity.type} - {entity.status}
                      </p>
                      <h3 className="text-lg font-semibold">{entity.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        Priority {entity.priority}
                        {entity.systems.length > 0
                          ? ` - ${entity.systems.join(", ")}`
                          : ""}
                        {entity.assignment?.assigneeId
                          ? ` - Owner ${entity.assignment.assigneeId}`
                          : ""}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => navigate(`/entities/${entity.id}`)}
                      >
                        Open work item
                      </Button>
                      {lensKey === "execution" ? (
                        <Button
                          variant="ghost"
                          onClick={() => setRationaleEntity(entity)}
                        >
                          Rationale
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))}
                {visibleCount < lensFilteredEntities.length ? (
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => setVisibleCount((count) => count + 20)}
                  >
                    Load more
                  </Button>
                ) : null}
              </>
            )}
          </CardContent>
        </Card>
      </section>
      <RoutingRationaleSheet
        open={Boolean(rationaleEntity)}
        entityId={rationaleEntity?.id ?? null}
        entityTitle={rationaleEntity?.title}
        threadId={rationaleEntity?.threadId}
        onClose={() => setRationaleEntity(null)}
        navigate={navigate}
      />
    </PageShell>
  );
}
